import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/auth.store'
import LoginPage  from './pages/auth/LoginPage'
import PinPage    from './pages/auth/PinPage'
import AppShell   from './components/layout/AppShell'

// Lazy-loaded pages
const DashboardPage      = lazy(() => import('./pages/dashboard/DashboardPage'))
const ChildrenPage       = lazy(() => import('./pages/children/ChildrenPage'))
const ChildProfilePage   = lazy(() => import('./pages/children/ChildProfilePage'))
const CurriculumPage     = lazy(() => import('./pages/curriculum/CurriculumPage'))
const SchedulePage       = lazy(() => import('./pages/schedule/SchedulePage'))
const LessonsPage        = lazy(() => import('./pages/lessons/LessonsPage'))
const HomeworkPage       = lazy(() => import('./pages/homework/HomeworkPage'))
const AssessmentsPage    = lazy(() => import('./pages/assessments/AssessmentsPage'))
const ReportsPage        = lazy(() => import('./pages/reports/ReportsPage'))
const MeritsPage         = lazy(() => import('./pages/merits/MeritsPage'))
const AiModulePage       = lazy(() => import('./pages/ai/AiModulePage'))
const SettingsPage       = lazy(() => import('./pages/settings/SettingsPage'))
const WebsiteEditorPage  = lazy(() => import('./pages/website/WebsiteEditorPage'))
const HelpPage           = lazy(() => import('./pages/help/HelpPage'))

function Loading() {
  return <div className="p-8 text-gray-400 text-sm animate-pulse">Loading…</div>
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />
}

function Page({ component: Component }: { component: React.ComponentType }) {
  return (
    <Suspense fallback={<Loading />}>
      <Component />
    </Suspense>
  )
}

export const router = createBrowserRouter([
  // Public routes
  { path: '/login',     element: <LoginPage /> },
  { path: '/login/pin', element: <PinPage /> },

  // Protected app routes
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // Core
      { path: 'dashboard',          element: <Page component={DashboardPage} /> },
      { path: 'children',           element: <Page component={ChildrenPage} /> },
      { path: 'children/:id',       element: <Page component={ChildProfilePage} /> },
      { path: 'curriculum',         element: <Page component={CurriculumPage} /> },

      // Phase 2
      { path: 'schedule',           element: <Page component={SchedulePage} /> },
      { path: 'lessons/:id',        element: <Page component={LessonsPage} /> },
      { path: 'lessons',            element: <Navigate to="/schedule" replace /> },
      { path: 'homework',           element: <Page component={HomeworkPage} /> },

      // Phase 3
      { path: 'assessments',        element: <Page component={AssessmentsPage} /> },
      { path: 'reports',            element: <Page component={ReportsPage} /> },
      { path: 'merits',             element: <Page component={MeritsPage} /> },

      // Phase 4
      { path: 'ai',                 element: <Page component={AiModulePage} /> },

      // Phase 5 — Website CMS
      { path: 'website',            element: <Page component={WebsiteEditorPage} /> },

      // Settings
      { path: 'settings',           element: <Page component={SettingsPage} /> },

      // Help
      { path: 'help',               element: <Page component={HelpPage} /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
])
